import axios from 'axios';
import config from '../../coreFIles/config';
import { v4 as uuidv4 } from 'uuid';
import { insertBookingData, confirmHotelBooking } from "../../Action/user.action";

let correlationId = uuidv4();

export const getCorrelationId = () => correlationId;
export const resetCorrelationId = () => {
  correlationId = uuidv4();
  return correlationId;
};

export const getZentrumSuggestions = async (searchText) => {
  try {
    const response = await axios.get(`${config.autosuggestUrl}/locationcontent/autosuggest`, {
      params: { term: searchText }
    });
    resetCorrelationId()

    return response.data.locationSuggestions || [];
  } catch (error) {
    console.error('ZentrumHub Autosuggest Error:', error);
    return [];
  }
};


export const getZentrumLocationDetails = async (locationId) => {
  try {
    const response = await axios.get(`${config.autosuggestUrl}/locationcontent/location/${locationId}`, {
      params: { getSublocations: false }
    });
    console.log('ZentrumHub Location Details Response:', response.data);
    return response.data || {};
  } catch (error) {
    console.error('ZentrumHub Location Detail Error:', error);
    return null;
  }
};

const getUserIP = async () => {
  try {
    const response = await axios.get('https://api.ipify.org?format=json');
    return response.data.ip;
  } catch (error) {
    console.error('Error fetching IP address:', error);
    return '';
  }
};

export const initHotelSearch = async ({
  locationData,
  checkInDate,
  checkOutDate,
  occupancies
}) => {
  try {
    const requestBody = {
      channelId: config.channelId,
      currency: 'USD',
      culture: 'en-US',
      checkIn: checkInDate,
      checkOut: checkOutDate,
      occupancies: occupancies,
      searchLocationDetails: {
        id: locationData.id,
        name: locationData.name,
        fullName: locationData.fullName,
        type: locationData.type,
        state: locationData.state,
        country: locationData.country,
        coordinates: locationData.coordinates
      },
      nationality: 'US',
      countryOfResidence: 'US',
      destinationCountryCode: 'US',
      travelPurpose: 'Leisure',
      filterBy: null
    };

    if (locationData.boundaries && locationData.boundaries.length > 0) {
      requestBody.polygonalRegion = {
        coordinates: locationData.boundaries[0]
      };
    } else if (locationData.coordinates) {
      requestBody.circularRegion = {
        centerLat: locationData.coordinates.lat,
        centerLong: locationData.coordinates.long,
        radiusInKm: 30
      };
    } else {
      throw new Error('Invalid location data - neither boundaries nor coordinates provided');
    }

    console.log('Hotel Search Request Body:', requestBody);

    const userIP = await getUserIP();

    const response = await axios.post(
      `${config.hotelUrl}/availability/init`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );

    return response.data;

  } catch (error) {
    console.error('Error initializing hotel search:', error.response?.data || error.message);
    return null;
  }
};

export const getHotelSearchResults = async (token) => {
  try {

    const userIP = await getUserIP();

    const response = await axios.get(
      `${config.hotelUrl}/availability/async/${token}/results`,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );

    console.log('Hotel Search Results Response:', response.data);

    return response.data;

  } catch (error) {
    console.error('Error fetching hotel search results:', error.response?.data || error.message);
    return null;
  }
};


export const getHotelContent = async (hotelIds = []) => {
  try {
    if (hotelIds.length === 0) {
      console.error('Hotel IDs array is empty.');
      return null;
    }

    const userIP = await getUserIP();

    const requestBody = {
      hotelIds: hotelIds,
      channelId: config.channelId,
      culture: 'en-US',
      contentFields: ['All']
    };

    const response = await axios.post(
      `${config.contentUrl}/hotelcontent/getHotelContent`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );

    console.log('Hotel Content Response:', response.data);
    return response.data;

  } catch (error) {
    console.error('Error fetching hotel content:', error.response?.data || error.message);
    return null;
  }
};


export const getRoomsAndRates = async (hotelId, token) => {
  try {
    const userIP = await getUserIP();

    const requestBody = {
      searchSpecificProviders: false
    };

    const response = await axios.post(
      `${config.hotelUrl}/${hotelId}/roomsandrates/${token}`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );

    return response.data;

  } catch (error) {
    console.error('Error fetching rooms and rates:', error.response?.data || error.message);
    return null;
  }
};


export const getNextHotelResults = async (token, nextResultsKey) => {
  try {
    const userIP = await getUserIP();

    const response = await axios.get(
      `${config.hotelUrl}/availability/async/${token}/results`,
      {
        params: { nextResultsKey },
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );

    console.log('Next Results Response:', response.data);
    return response.data;

  } catch (error) {
    console.error('Error fetching next hotel results:', error.response?.data || error.message);
    return null;
  }
};

export const priceRecommendation = async (data) => {
  try {
    if (!data || !data.hotelId || !data.token || !data.reccommendedRoomId) {
      return null;
    }
    const userIP = await getUserIP();
    let hotelId = data.hotelId;
    let token = data.token;
    let recommendationId = data.reccommendedRoomId;
    const response = await axios.get(
      `${config.hotelUrl}/${hotelId}/${token}/price/recommendation/${recommendationId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );

    return response.data;

  } catch (error) {
    console.error('Error fetching rooms and rates:', error.response?.data || error.message);
    return null;
  }
};

export const bookInit = async (bookingData) => {
  try {
    const userIP = await getUserIP();

    let hotelId = bookingData.hotelId;
    let token = bookingData.token;
    let propertyData = bookingData.propertyData;


    const requestBody = {
      rateIds: [bookingData.rateIds],
      bookingRefId: bookingData.bookingRefId,
      specialRequests: [
        "Need an early check-in"
      ],
      roomsAllocations: [
        {
          "roomid": bookingData.roomId,
          "rateid": bookingData.rateIds,
          "guests": bookingData.guestData.guestName.map(guest => ({
            "type": "Adult",
            "title": guest.title,
            "firstname": guest.firstName,
            "lastname": guest.lastName,
            "age": 25, // Default age since not provided
            "email": bookingData.guestData.email
          }))
        }
      ],
      billingContact: {
        "title": bookingData.guestData.guestName[0].title,
        "firstName": bookingData.guestData.guestName[0].firstName,
        "lastName": bookingData.guestData.guestName[0].lastName,
        "age": 25, // Default age since not provided
        "contact": {
          "phone": bookingData.guestData.phone,
          "address": {
            "line1": bookingData.guestData.addressLine1,
            "line2": bookingData.guestData.addressLine2,
            "city": {
              "name": bookingData.guestData.city,
              "code": "" // Not provided
            },
            "state": {
              "name": bookingData.guestData.state,
              "code": "" // Not provided
            },
            "country": {
              "name": bookingData.guestData.country,
              "code": bookingData.guestData.countryCode
            },
            "postalCode": bookingData.guestData.zipCode
          },
          "email": bookingData.guestData.email
        }
      },
      creditCard: {
        "issuer": "VI",
        "number": '2365987412563258',
        "cvv": '123',
        "nameoncard": 'test',
        "expirymonth": "05",
        "expiryyear": "2030",
        "contact": {
          "email": bookingData.guestData.email,
          "phone": bookingData.guestData.phone,
          "billingaddress": {
            "line1": bookingData.guestData.addressLine1,
            "line2": bookingData.guestData.addressLine2,
            "city": {
              "name": bookingData.guestData.city,
              "code": ""
            },
            "state": {
              "name": bookingData.guestData.state,
              "code": ""
            },
            "country": {
              "name": bookingData.guestData.country,
              "code": bookingData.guestData.countryCode
            },
            "postalCode": bookingData.guestData.zipCode
          }
        }
      }
    };

    const response = await axios.post(
      `${config.hotelUrl}/${hotelId}/${token}/bookinit`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );
    console.log('Book Init Response:', response.data);

    if (response.data.bookingStatus == 'Initiated') {
      await insertBookingData(bookingData)
      return response.data;

    }

  } catch (error) {
    console.error('Error during book init:', error.response?.data || error.message);
    return null;
  }
};


export const hotelBooking = async (bookingData) => {
  try {
    const userIP = await getUserIP();

    let hotelId = bookingData.hotelId;
    let token = bookingData.token;

    const requestBody = {
      rateIds: [bookingData.rateIds],
      bookingRefId: bookingData.bookingRefId,
      specialRequests: [
        "Need an early check-in"
      ],
      roomsAllocations: [
        {
          "roomid": bookingData.roomId,
          "rateid": bookingData.rateIds,
          "guests": bookingData.guestData.guestName.map(guest => ({
            "type": "Adult",
            "title": guest.title,
            "firstname": guest.firstName,
            "lastname": guest.lastName,
            "age": 25, // Default age since not provided
            "email": bookingData.guestData.email
          }))
        }
      ],
      billingContact: {
        "title": bookingData.guestData.guestName[0].title,
        "firstName": bookingData.guestData.guestName[0].firstName,
        "lastName": bookingData.guestData.guestName[0].lastName,
        "age": 25, // Default age since not provided
        "contact": {
          "phone": bookingData.guestData.phone,
          "address": {
            "line1": bookingData.guestData.addressLine1,
            "line2": bookingData.guestData.addressLine2,
            "city": {
              "name": bookingData.guestData.city,
              "code": "" // Not provided
            },
            "state": {
              "name": bookingData.guestData.state,
              "code": "" // Not provided
            },
            "country": {
              "name": bookingData.guestData.country,
              "code": bookingData.guestData.countryCode
            },
            "postalCode": bookingData.guestData.zipCode
          },
          "email": bookingData.guestData.email
        }
      },
      creditCard: {
        "issuer": "VI",
        "number": '2365987412563258',
        "cvv": '123',
        "nameoncard": 'test',
        "expirymonth": "05",
        "expiryyear": "2030",
        "contact": {
          "email": bookingData.guestData.email,
          "phone": bookingData.guestData.phone,
          "billingaddress": {
            "line1": bookingData.guestData.addressLine1,
            "line2": bookingData.guestData.addressLine2,
            "city": {
              "name": bookingData.guestData.city,
              "code": ""
            },
            "state": {
              "name": bookingData.guestData.state,
              "code": ""
            },
            "country": {
              "name": bookingData.guestData.country,
              "code": bookingData.guestData.countryCode
            },
            "postalCode": bookingData.guestData.zipCode
          }
        }
      }
    };

    console.log('Book Request Body:', requestBody);

    const response = await axios.post(
      `${config.hotelUrl}/${hotelId}/${token}/book`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );
    console.log('Book Response:', response.data);
    if (response.data.bookingStatus == 'Confirmed') {
      bookingData.paymentResponse = response.data
      await confirmHotelBooking(bookingData)
      return response.data;
    } else {
      return
    }
  } catch (error) {
    console.error('Error during book:', error.response?.data || error.message);
    return null;
  }
};


export const getBookingDetail = async (bookingData) => {
  try {
    const userIP = await getUserIP();

    const requestBody = {
      channelId: config.channelId,
      bookingId: bookingData.bookingId
    };

    const response = await axios.post(
      `${config.hotelUrl}/getBookingDetails`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );
    console.log('booking detail', response.data);
    return response.data;
  } catch (error) {
    console.error('Error during get hotel detail:', error.response?.data || error.message);
    return null;
  }
};

export const checkCancellationFees = async (bookingId) => {
  try {

    const userIP = await getUserIP();

    const response = await axios.get(
      `${config.hotelUrl}/bookingcancellationFee?id=${bookingId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );

    console.log('Hotel Cancel Results Response:', response.data);

    return response.data;

  } catch (error) {
    console.error('Error during cancel hotel:', error.response?.data || error.message);
    return null;
  }
};

export const cancelHotelBooking = async (bookingId) => {
  try {
    const userIP = await getUserIP();

    const response = await axios.post(
      `${config.hotelUrl}/booking/${bookingId}/cancel`,
      {
        headers: {
          'Content-Type': 'application/json',
          'apiKey': config.apiKey,
          'accountId': config.accountId,
          'customer-ip': userIP,
          'correlationId': getCorrelationId()
        }
      }
    );
    console.log('cancellation detail', response.data);
    return response.data;
  } catch (error) {
    console.error('Error during cancel hotel booking:', error.response?.data || error.message);
    return null;
  }
};